import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useTranslation } from 'react-i18next';
import { androidColors } from '../../theme/androidTheme';
import { CryptoData } from '../../services/cryptoService';

interface AllocationChartProps {
  cryptos: CryptoData[];
  title?: string;
}

const AllocationChart: React.FC<AllocationChartProps> = ({ 
  cryptos, 
  title 
}) => {
  const { t } = useTranslation();
  const chartTitle = title || t('portfolio.allocation');
  // Couleurs pour le graphique
  const chartColors = [
    androidColors.primaryGold,
    '#4A90E2',
    '#50C878',
    '#FF8C00',
    '#9370DB',
    '#DC143C',
    '#20B2AA',
    '#FF6347',
    '#32CD32',
    '#FF1493',
  ];

  // Préparer les données pour le graphique
  const prepareChartData = () => {
    if (!cryptos || cryptos.length === 0) return [];

    let totalValue = 0;
    const cryptoValues = cryptos.map(crypto => {
      const value = crypto.value_current;
      totalValue += value;
      return { ...crypto, value };
    });

    return cryptoValues
      .filter(crypto => crypto.value > 0)
      .map((crypto, index) => ({
        name: crypto.symbol.toUpperCase(),
        fullName: crypto.name,
        value: crypto.value,
        percentage: totalValue > 0 ? (crypto.value / totalValue) * 100 : 0,
        color: chartColors[index % chartColors.length],
        amount: crypto.amount,
        price_current: crypto.price_current,
      }))
      .sort((a, b) => b.value - a.value); // Trier par valeur décroissante
  };

  const chartData = prepareChartData();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
            {data.name} - {data.fullName}
          </Typography>
          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
            Valeur: {formatCurrency(data.value)}
          </Typography>
          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
            Pourcentage: {data.percentage.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
            Quantité: {data.amount}
          </Typography>
          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
            Prix: {formatCurrency(data.price_current)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
          {payload.map((entry: any, index: number) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: `${androidColors.primaryGold}10`,
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: entry.color,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: androidColors.textColor,
                    fontWeight: 'bold',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.payload.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: androidColors.textSecondary,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {entry.payload.percentage.toFixed(1)}% • {formatCurrency(entry.payload.value)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (chartData.length === 0) {
    return (
      <Card
        sx={{
          backgroundColor: androidColors.cardBackground,
          border: `1px solid ${androidColors.borderColor}`,
          borderRadius: '12px',
        }}
      >
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
              mb: 2,
            }}
          >
            {chartTitle}
          </Typography>
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: androidColors.textSecondary,
            }}
          >
            <Typography variant="body1">
              Aucune donnée à afficher
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Ajoutez des cryptomonnaies à votre portefeuille
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        backgroundColor: androidColors.cardBackground,
        border: `1px solid ${androidColors.borderColor}`,
        borderRadius: '12px',
      }}
    >
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 2,
          }}
        >
          {title}
        </Typography>
        
        <Box sx={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AllocationChart;